import { Button, Heading, Icon, useCopyToClipboard } from '@dittolive/anvil'
import { CheckIcon, CopyIcon } from '@phosphor-icons/react'
import * as Sentry from '@sentry/browser'
import graphql from 'babel-plugin-relay/macro'
import cx from 'classnames'
import useAppAuthentication from 'components/authenticationWebhooks'
import { getPlaygroundProvider } from 'components/authenticationWebhooks/useAppAuthentication'
import { useAppToDatabaseRename } from 'components/hooks'
import { Link } from 'components/link'
import paths from 'paths'
import { Suspense, useContext, useEffect } from 'react'
import { useFragment } from 'react-relay/hooks'
import { useLocation, useParams } from 'react-router-dom'
import { useDeviceByPeerKey } from 'scenes/appManagement/hooks'
import { useFeatureFlags } from 'utils/featureFlags'

import { AppHeader_app$key } from './__generated__/AppHeader_app.graphql'
import AppForgeLink from './AppForgeLink'
import AppSettingsContext from './context'
import IngressDomainLoadingStatus from './IngressDomainLoadingStatus'

const fragment = graphql`
  fragment AppHeader_app on App {
    id
    name
    slug
    tenant {
      ... on Organization {
        # eslint-disable-next-line relay/unused-fields
        id
        name
        slug
        __typename
      }
      ... on User {
        # eslint-disable-next-line relay/unused-fields
        id
        email
        firstName
        lastName
        __typename
      }
    }
  }
`

type Props = {
  /** App header key. */
  app: AppHeader_app$key | null
}

/** Renders a header for the organization pages */
const AppHeader = ({ app }: Props) => {
  const data = useFragment(fragment, app)
  const params = useParams<{ userId?: string }>()
  const location = useLocation()
  const peerKey = location.pathname.match(/\/devices\/([^/]+)$/)?.[1]

  const { isDomainReady, cloudDomainUrl } = useContext(AppSettingsContext)
  const { device } = useDeviceByPeerKey({
    appId: data?.id ?? '',
    cloudUrl: cloudDomainUrl,
    peerKey: peerKey ?? '',
  })
  const { configuration, isAuthEnabled } = useAppAuthentication()
  const { appForge } = useFeatureFlags()
  const { authModeText, idText, tokenText, isRenamed } =
    useAppToDatabaseRename()

  const playgroundToken: string =
    getPlaygroundProvider(configuration)?.sharedToken ?? ''

  const [doCopyAppId, appIdRecentlyCopied, canCopyAppId] = useCopyToClipboard(
    data?.id ?? '',
  )
  const [
    doCopyPlaygroundToken,
    playgroundTokenRecentlyCopied,
    canCopyPlaygroundToken,
  ] = useCopyToClipboard(playgroundToken)

  const getGroupLink = () => {
    if (data?.tenant.__typename === 'Organization') {
      return paths.organizationManagement({ slug: data.tenant.slug, isRenamed })
    }
    if (data?.tenant.__typename === 'User') {
      return !!params.userId
        ? paths.userApps({
            userId: params.userId,
            isRenamed,
          })
        : paths.gettingStarted()
    }

    return ''
  }

  const getAppLink = () => {
    if (!data) {
      return ''
    }

    if (data.tenant.__typename === 'Organization') {
      return paths.app({
        organizationSlug: data.tenant.slug,
        appSlug: data.slug,
        isRenamed,
      })
    }

    if (data.tenant.__typename === 'User') {
      return paths.app({
        tenantId: params.userId,
        appSlug: data.slug,
        isRenamed,
      })
    }

    return ''
  }

  const userName = () => {
    if (data?.tenant.__typename === 'User') {
      return data.tenant.firstName || data.tenant.lastName
        ? `${data.tenant.firstName} ${data.tenant.lastName}`
        : data.tenant.email
    }

    return ''
  }

  const truncateString = (str: string) => {
    const start = str.slice(0, 5)
    const end = str.slice(-5)
    return `${start}...${end}`
  }

  /** Report missing fragment key or data to help debug SURF-292. */
  useEffect(() => {
    if (!app || !data) {
      Sentry.captureMessage(`Failed to load AppHeader_app fragment`, {
        level: 'warning',
        tags: {
          appProp: JSON.stringify(app),
          fragmentData: JSON.stringify(data),
        },
      })
    }
  }, [app, data])

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex flex-col justify-between pb-6 pt-4 lg:flex-row">
          <div className="mb-4 px-4 lg:mb-0 lg:px-0">
            <Link
              data-testid="groupLink"
              to={getGroupLink()}
              className="text-foreground-accent focus-visible:outline-focus-outline mb-1 inline-block text-sm outline-none hover:opacity-80 focus-visible:outline-2"
            >
              {data?.tenant.__typename === 'Organization' &&
                `${data.tenant.name} /`}
              {data?.tenant.__typename === 'User' && `${userName()} /`}
            </Link>
            {peerKey && data && (
              <Link
                data-testid="appBreadcrumbLink"
                to={getAppLink()}
                className="text-foreground-accent focus-visible:outline-focus-outline mb-1 ml-1 inline-block text-sm outline-none hover:opacity-80 focus-visible:outline-2"
              >
                {data.name} /
              </Link>
            )}
            <Heading level={1}>{device?.name ?? data?.name}</Heading>
          </div>

          {isDomainReady && (
            <div
              className="scrollbar-hide flex items-end space-x-8 overflow-x-auto px-4 lg:overflow-x-visible lg:px-0"
              data-testid="appMetaData"
            >
              {configuration && (
                <div data-testid="appAuthMode">
                  <span className="text-foreground-subtle mb-2 block whitespace-nowrap text-sm">
                    Auth mode
                  </span>
                  <span
                    className={cx(
                      'text-foreground-normal flex h-6 items-center whitespace-nowrap border-l-2 pl-1.5 text-sm',
                      {
                        'border-green-500': isAuthEnabled,
                        'border-amber-500': !isAuthEnabled,
                      },
                    )}
                  >
                    {isAuthEnabled ? 'Authentication enabled' : authModeText}
                  </span>
                </div>
              )}

              <div>
                <span className="text-foreground-subtle mb-2 block whitespace-nowrap text-sm">
                  {idText}
                </span>
                <span className="text-foreground-normal flex h-6 items-center whitespace-nowrap text-sm">
                  <span title={data?.id} className="font-plex-mono">
                    {truncateString(data?.id || '')}
                  </span>

                  {canCopyAppId && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={doCopyAppId}
                      className="focus-visible:outline-focus-outline group ml-1 outline-none focus-visible:outline-2"
                    >
                      {appIdRecentlyCopied ? (
                        <Icon
                          svg={<CheckIcon />}
                          className="text-fill-success size-4"
                        />
                      ) : (
                        <Icon
                          svg={<CopyIcon />}
                          className="text-foreground-subtle hover:text-foreground-normal group-focus-visible:text-foreground-normal size-4"
                        />
                      )}
                    </Button>
                  )}
                </span>
              </div>

              {playgroundToken && (
                <div data-testid="appPlaygroundToken">
                  <span className="text-foreground-subtle mb-2 block whitespace-nowrap text-sm">
                    {tokenText}
                  </span>
                  <span className="text-foreground-normal flex h-6 items-center whitespace-nowrap text-sm">
                    <span title={playgroundToken} className="font-plex-mono">
                      {truncateString(playgroundToken)}
                    </span>

                    {canCopyPlaygroundToken && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={doCopyPlaygroundToken}
                        className="focus-visible:outline-focus-outline group ml-1 outline-none focus-visible:outline-2"
                      >
                        {playgroundTokenRecentlyCopied ? (
                          <Icon
                            svg={<CheckIcon />}
                            className="text-fill-success size-4"
                          />
                        ) : (
                          <Icon
                            svg={<CopyIcon />}
                            className="text-foreground-subtle hover:text-foreground-normal group-focus-visible:text-foreground-normal size-4"
                          />
                        )}
                      </Button>
                    )}
                  </span>
                </div>
              )}

              {appForge && data?.id && (
                <Suspense>
                  <AppForgeLink databaseId={data.id} />
                </Suspense>
              )}
            </div>
          )}
        </div>
        <div className="px-4 lg:mb-0 lg:px-0">
          <IngressDomainLoadingStatus />
        </div>
      </div>
    </div>
  )
}

export default AppHeader
